import { ItemType } from 'data/types';
import {
  SpaceTypeGroups,
  SpaceTypeGroupsInput
} from 'utils/space-type-groups-types';
import { isDetachedSpaceItem } from 'utils/structure';

const CONDITIONED_COMMON_SPACE_CODES = [
  'primary_bedroom',
  'bedroom',
  'bathroom',
  'kitchen',
  'dining_room',
  'living_room',
  'family_room',
  'garage_attached',
  'home_office',
  'open_space',
  'stairway',
  'custom'
];

const UNCONDITIONED_COMMON_SPACE_CODES = ['open_space', 'stairway', 'custom'];

export function getCommonSpaceCodes(isConditioned: boolean): string[] {
  return isConditioned
    ? CONDITIONED_COMMON_SPACE_CODES
    : UNCONDITIONED_COMMON_SPACE_CODES;
}

function isSpaceItem(item: ItemType): boolean {
  return item.group === 'Space';
}

function matchesSearch(item: ItemType, searchText: string): boolean {
  return (
    searchText.length === 0 ||
    item.label.toLowerCase().includes(searchText.toLowerCase())
  );
}

function findSpaceItemByCode(
  items: ItemType[],
  code: string
): ItemType | undefined {
  return items.find((item) => isSpaceItem(item) && item.code === code);
}

function isPresent(item: ItemType | undefined): item is ItemType {
  return item != null;
}

export function groupSpaceTypeItems({
  items,
  isConditioned,
  suggestedSpaces,
  hasSuggestions,
  searchText
}: SpaceTypeGroupsInput): SpaceTypeGroups {
  const commonSpaceCodes = getCommonSpaceCodes(isConditioned);
  const isSuggested = (item: ItemType) =>
    !!suggestedSpaces && suggestedSpaces.includes(item.code);

  const suggested =
    hasSuggestions && suggestedSpaces
      ? [...new Set(suggestedSpaces)]
          .map((code) => findSpaceItemByCode(items, code))
          .filter(isPresent)
          .filter((item) => matchesSearch(item, searchText))
      : [];

  const common = commonSpaceCodes
    .map((code) => findSpaceItemByCode(items, code))
    .filter(isPresent)
    .filter((item) => !isSuggested(item) && matchesSearch(item, searchText));

  const nonCommon = items.filter(
    (item) =>
      isSpaceItem(item) &&
      !commonSpaceCodes.includes(item.code) &&
      !isSuggested(item) &&
      matchesSearch(item, searchText)
  );

  const detached = nonCommon.filter((item) => isDetachedSpaceItem(item.id));
  const other = nonCommon
    .filter((item) => !isDetachedSpaceItem(item.id))
    .sort((a, b) => a.label.localeCompare(b.label));

  return { suggested, common, other, detached };
}

export function hasAnySpaceTypeItems(groups: SpaceTypeGroups): boolean {
  return (
    groups.suggested.length > 0 ||
    groups.common.length > 0 ||
    groups.other.length > 0 ||
    groups.detached.length > 0
  );
}
